import {Button, IconButton} from '@dropbox/dig-components/buttons';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Split} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {EditLine, GratitudeLine, SoundOnLine} from '@dropbox/dig-icons/assets';
import {analyticsLogger} from 'analytics/analyticsLogger';
import {pulseUserAtom} from 'atoms/auth';
import {isMobileAtom} from 'atoms/layout';
import {Employee, EmployeeEventSubscriptionStates, EmployeeFull, TeamWithCountsNew} from 'client';
import {Avatar} from 'components/DSYS/Avatar';
import {TeamHoverCard} from 'components/DSYS/HoverCard';
import {ButtonLink, Link} from 'components/DSYS/Link';
import {Title} from 'components/DSYS/Title';
import {isProd} from 'helpers/environment';
import {isSuperUser} from 'helpers/utils';
import {t} from 'i18next';
import {useAtomValue} from 'jotai';
import {Fragment, useMemo, useState} from 'react';
import {useNavigate} from 'react-router';

import {ProfileAskChips} from './ProfileAskChips';
import styles from './ProfileBody.module.css';
import {ProfileConfetti} from './ProfileConfetti';
import {ProfileDetails} from './ProfileDetails';
import {sortEmployeeTeams} from './sortEmployeeTeams';

interface ProfileBodyProps {
  publicAccountId?: string | null;
  employee: EmployeeFull;
  employeeTeams: TeamWithCountsNew[];
  eventSubscriptions: EmployeeEventSubscriptionStates;
  showPto: boolean;
  admin?: Employee | null;
  supportedLeaders?: Employee[];
  disableConfetti?: boolean;
  onEditTeams?: () => void;
  /** Offer Ask questions about this person (profile Ask flag). */
  showAsk?: boolean;
  /** The profile's reporting line, the person first; shapes the Ask questions. */
  reportingLine?: Employee[];
}

const currentDate = new Date();
const currentMonth = String(currentDate.getMonth() + 1).padStart(2, '0');
const currentDay = String(currentDate.getDate()).padStart(2, '0');
const formattedCurrentDate = `${currentMonth}/${currentDay}`;

const decodePublicAccountId = (value: string | null | undefined) => {
  if (!value) {
    return null;
  }

  try {
    return atob(value);
  } catch {
    return value;
  }
};

const getConfettiType = (employee: EmployeeFull) => {
  if (employee.is_deactivated) {
    return undefined;
  }

  if (employee.birthday === formattedCurrentDate) {
    return 'birthday';
  }

  if (employee?.tenure != null && employee.tenure > 0 && employee.tenure % 12 == 0) {
    return 'anniversary';
  }

  return undefined;
};

export const ProfileBody = (props: ProfileBodyProps) => {
  const isMobile = useAtomValue(isMobileAtom);
  const pulseUser = useAtomValue(pulseUserAtom);

  const [isProfileDetailsHover, setIsProfileDetailsHover] = useState(!isProd);

  const profileActionLog = (action: string) =>
    analyticsLogger().logEvent('PROFILE_INTERACTION', {action});
  const navigate = useNavigate();
  const gratitudeHref = `/gratitude?to=${props.employee.ldap}`;

  const confettiType = props.disableConfetti ? undefined : getConfettiType(props.employee);

  const isSuperAdmin = isSuperUser(pulseUser);
  const showPublicAccountId = isSuperAdmin && (isProd ? isProfileDetailsHover : true);
  const canEditTeam = pulseUser?.email === props.employee.email;
  const sortedEmployeeTeams = useMemo(
    () => sortEmployeeTeams(props.employee, props.employeeTeams),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.employee.team_associations, props.employeeTeams]
  );
  // Hide team memberships on deactivated profiles to match the Teams page,
  // which excludes deactivated employees from member lists.
  const showTeams = !props.employee.is_deactivated && sortedEmployeeTeams.length > 0;

  return (
    <div className={styles.profileBodySection}>
      {confettiType && <ProfileConfetti ldap={props.employee.ldap} type={confettiType} />}
      <div className={styles.profileNameAvatar}>
        <Box position="relative">
          <Avatar
            user={props.employee}
            isInactive={Boolean(props.employee.is_deactivated)}
            onLeave={props.employee.on_leave}
            size="profile"
            key={props.employee.ldap}
          />
        </Box>
        <div className={styles.profileNameCard}>
          <div className={styles.profileNameHeader}>
            <Title
              size={24}
              withAccessoryEnd={
                <>
                  {window.speechSynthesis && props.employee.pronunciation && (
                    <>
                      <IconButton
                        variant="borderless"
                        onClick={() => {
                          profileActionLog('pronunciation');
                          window.speechSynthesis.speak(
                            new SpeechSynthesisUtterance(props.employee.pronunciation!)
                          );
                        }}
                        aria-label="icon button"
                      >
                        <UIIcon src={SoundOnLine} className={atoms({color: 'Text Subtle'})} />
                      </IconButton>{' '}
                    </>
                  )}
                  {props.employee.pronouns && (
                    <Text size="small" color="faint">
                      {props.employee.pronouns}
                    </Text>
                  )}
                </>
              }
            >
              {props.employee.name !== ' ' ? props.employee.name : props.employee.ldap}
            </Title>
          </div>
          <Box
            onMouseEnter={() => {
              if (isSuperAdmin && isProd) {
                setIsProfileDetailsHover(true);
              }
            }}
            onMouseLeave={() => {
              if (isSuperAdmin && isProd) {
                setIsProfileDetailsHover(false);
              }
            }}
          >
            <Text size="large" color="faint">
              {props.employee.role}
              {props.employee.type == 'Regular' &&
                props.employee.level &&
                ` (${props.employee.level})`}
            </Text>
            {props.employee.type !== 'Regular' && (
              <Text size="large" color="faint">
                {''} ({props.employee.type})
              </Text>
            )}
            {showTeams && (
              <Text size="large" color="faint">
                {' '}
                on{' '}
              </Text>
            )}
            {showTeams &&
              sortedEmployeeTeams.map(
                (team, index) =>
                  team && (
                    <Fragment key={team.slug}>
                      <Text size="large" color="faint">
                        {index > 0 && <strong>{', '}</strong>}
                        <TeamHoverCard
                          slug={team.slug ?? ''}
                          name={team.name ?? ''}
                          description={team.description}
                          memberCount={team.total_employee_count}
                          primaryContactName={team.primary_contact}
                        >
                          <Link
                            to={`/teams/${team.slug}`}
                            onClick={() => profileActionLog('team')}
                            monochromatic
                            hasNoUnderline
                            showUnderlineOnHover
                            isBold
                          >
                            {team.name}
                          </Link>
                        </TeamHoverCard>
                      </Text>
                    </Fragment>
                  )
              )}
            {canEditTeam && Boolean(props.employeeTeams?.length) && (
              <IconButton
                onClick={() => props.onEditTeams?.()}
                variant="borderless"
                style={{marginBottom: 6}}
                aria-label="icon button"
              >
                <UIIcon src={EditLine} />
              </IconButton>
            )}
            {!isProd && isSuperAdmin && <Text color="faint"> • {props.employee.user_id}</Text>}
            {showPublicAccountId && props.publicAccountId && (
              <Box>
                <Text color="faint">{decodePublicAccountId(props.publicAccountId)}</Text>
              </Box>
            )}
          </Box>
          {canEditTeam && !props.employeeTeams?.length && (
            <Button
              className={atoms({marginTop: '8'})}
              onClick={() => props.onEditTeams?.()}
              variant="outline"
              withIconStart={<UIIcon src={EditLine} />}
            >
              {t('edit_teams')}
            </Button>
          )}
          {!props.employee.is_deactivated && props.employee.email !== pulseUser?.email && (
            <Split
              gap={isMobile ? '4' : '12'}
              direction={isMobile ? 'vertical' : 'horizontal'}
              alignY="center"
            >
              {props.showAsk ? (
                <>
                  {/* The Ask chips lead; gratitude stays one click away as an icon. */}
                  <Split.Item>
                    <Box paddingTop="8">
                      <ProfileAskChips
                        person={{
                          employee_id: props.employee.user_id,
                          name: props.employee.name,
                          ldap: props.employee.ldap,
                        }}
                        reportingLine={props.reportingLine ?? []}
                        team={sortedEmployeeTeams[0]?.name}
                      />
                    </Box>
                  </Split.Item>
                  <Split.Item>
                    <Box paddingTop="8">
                      <Tooltip title={t('send_gratitude')}>
                        <IconButton
                          variant="outline"
                          size="small"
                          shape="circular"
                          aria-label={t('send_gratitude')}
                          onClick={() => {
                            profileActionLog('gratitude');
                            navigate(gratitudeHref);
                          }}
                        >
                          <UIIcon src={GratitudeLine} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Split.Item>
                </>
              ) : (
                <Split.Item>
                  <ButtonLink
                    to={gratitudeHref}
                    variant="outline"
                    withIconStart={<UIIcon src={GratitudeLine} />}
                    className={styles.gratitudeButton}
                    onClick={() => profileActionLog('gratitude')}
                  >
                    {t('send_gratitude')}
                  </ButtonLink>
                </Split.Item>
              )}
            </Split>
          )}
        </div>
      </div>
      {!props.employee.is_deactivated && (
        <ProfileDetails
          employee={props.employee}
          eventSubscriptions={props.eventSubscriptions}
          showPto={props.showPto}
          admin={props.admin}
          supportedLeaders={props.supportedLeaders}
          onClick={profileActionLog}
        />
      )}
    </div>
  );
};
